import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, MapPin, Clock, Wifi, Navigation, Coffee } from "lucide-react";
import { BUSINESS, IMG } from "../data/content";
import { LOCATIONS, SHARED_FAQS, SITE_URL, getLocation } from "../data/locations";
import { useOrder } from "../context/OrderContext";

const HIGHLIGHTS = [
  { icon: Wifi, label: "Fast, free WiFi" },
  { icon: Clock, label: "Open daily · 7:30 AM–10 PM" },
  { icon: Coffee, label: "Specialty coffee & Mediterranean kitchen" },
  { icon: MapPin, label: "946 N Jackson St, Arlington, VA" },
];

const Faq = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-surface/80" data-testid="location-faq-item">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-4 py-5 text-left"
        data-testid="location-faq-question"
      >
        <span className="text-base sm:text-lg text-foreground font-medium">{q}</span>
        <ChevronDown
          size={20}
          className={`shrink-0 text-highlight transition-transform duration-300 ${open ? "rotate-180" : ""}`}
        />
      </button>
      <div className={`grid transition-all duration-300 ${open ? "grid-rows-[1fr] pb-5" : "grid-rows-[0fr]"}`}>
        <p className="overflow-hidden text-foreground/70 leading-relaxed text-sm sm:text-base">{a}</p>
      </div>
    </div>
  );
};

const LocationPage = () => {
  const { slug } = useParams();
  const openOrder = useOrder();
  const loc = getLocation(slug);
  if (!loc) return <Navigate to="/" replace />;

  const faqs = [...loc.faqs, ...SHARED_FAQS];
  const url = `${SITE_URL}/coffee-shop/${loc.slug}`;
  const title = `Coffee Shop Near ${loc.name}, Arlington VA | Detour Coffee Co.`;
  const description = `${loc.lead} Detour Coffee Co. is ${loc.distance} at ${BUSINESS.address}. Specialty coffee, Mediterranean food & weekend brunch, open daily 7:30 AM–10 PM.`;

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "CafeOrCoffeeShop",
        "@id": `${url}#business`,
        name: `Detour Coffee Co. — near ${loc.name}`,
        image: IMG.spread,
        url,
        telephone: BUSINESS.phone,
        priceRange: "$$",
        address: {
          "@type": "PostalAddress",
          streetAddress: "946 N Jackson St",
          addressLocality: "Arlington",
          addressRegion: "VA",
          postalCode: "22201",
          addressCountry: "US",
        },
        geo: { "@type": "GeoCoordinates", latitude: 38.8841465, longitude: -77.0995974 },
        areaServed: { "@type": "Place", name: `${loc.name}, ${loc.region}` },
        servesCuisine: ["Coffee", "Mediterranean", "Brunch"],
        openingHoursSpecification: [
          {
            "@type": "OpeningHoursSpecification",
            dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
            opens: "07:30",
            closes: "22:00",
          },
        ],
      },
      {
        "@type": "FAQPage",
        "@id": `${url}#faq`,
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL + "/" },
          { "@type": "ListItem", position: 2, name: "Locations", item: SITE_URL + "/#areas" },
          { "@type": "ListItem", position: 3, name: loc.name, item: url },
        ],
      },
    ],
  };

  const others = LOCATIONS.filter((l) => l.slug !== loc.slug);

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={IMG.spread} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />

      <article className="bg-background" data-testid={`location-page-${loc.slug}`}>
        {/* Hero */}
        <header className="relative bg-[#241812] pt-32 pb-16 sm:pt-40 sm:pb-24 overflow-hidden">
          <div className="absolute inset-0 opacity-25">
            <img src={IMG.spread} alt="" className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#241812] via-[#241812]/70 to-[#241812]/40" />
          </div>
          <div className="relative max-w-4xl mx-auto px-6 md:px-12">
            <nav className="flex items-center gap-2 text-xs text-background/60 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-highlight transition-colors" data-testid="breadcrumb-home">Home</Link>
              <span>/</span>
              <Link to="/#areas" className="hover:text-highlight transition-colors">Areas We Serve</Link>
              <span>/</span>
              <span className="text-highlight">{loc.name}</span>
            </nav>
            <p className="text-xs sm:text-sm uppercase tracking-[0.24em] font-semibold text-highlight mb-4 flex items-center gap-3">
              <MapPin size={16} /> {loc.region}
            </p>
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="text-background text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
            >
              Your neighborhood <span className="text-highlight">coffee shop</span> near {loc.name}
            </motion.h1>
            <p className="mt-6 max-w-2xl text-background/80 text-base sm:text-lg leading-relaxed">{loc.lead}</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={openOrder}
                data-testid="location-order-btn"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-highlight text-foreground px-8 py-4 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03]"
              >
                Order Online <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={BUSINESS.directions}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-directions-btn"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-background px-8 py-4 text-sm font-semibold tracking-wide transition-colors hover:bg-white/10"
              >
                <Navigation size={16} /> Get Directions
              </a>
            </div>
          </div>
        </header>

        {/* Highlights */}
        <div className="border-b border-surface/70 bg-surface/40">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {HIGHLIGHTS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5 text-sm text-foreground/75">
                <Icon size={18} className="text-highlight shrink-0" />
                <span>{label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-14 sm:py-20">
          <div className="prose-detour space-y-6 text-foreground/80 text-base sm:text-lg leading-relaxed">
            {loc.paras.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>

          <h2 className="mt-14 text-2xl sm:text-3xl tracking-tight text-foreground">What locals order</h2>
          <p className="mt-4 text-foreground/80 leading-relaxed">
            Regulars from {loc.name} come for our{" "}
            <Link to="/#menu" className="text-highlight underline underline-offset-4 hover:opacity-80">Specialty Coffee</Link>{" "}
            — the Habibi Latte and Detour Delight are fan favorites — plus{" "}
            <Link to="/#menu" className="text-highlight underline underline-offset-4 hover:opacity-80">Mediterranean mezze</Link>,{" "}
            <Link to="/#menu" className="text-highlight underline underline-offset-4 hover:opacity-80">lazy pitas</Link> and{" "}
            <Link to="/#menu" className="text-highlight underline underline-offset-4 hover:opacity-80">weekend brunch</Link>.
            Planning something bigger? We handle{" "}
            <Link to="/#catering" className="text-highlight underline underline-offset-4 hover:opacity-80">catering</Link>{" "}
            across the area.
          </p>

          <h2 className="mt-14 text-2xl sm:text-3xl tracking-tight text-foreground">Getting here from {loc.name}</h2>
          <p className="mt-4 text-foreground/80 leading-relaxed">{loc.gettingHere}</p>
          <p className="mt-3 text-foreground/80 leading-relaxed">
            You'll find us {loc.metro}. See{" "}
            <Link to="/#contact" className="text-highlight underline underline-offset-4 hover:opacity-80">hours, map & contact details</Link>{" "}
            or browse the{" "}
            <Link to="/#gallery" className="text-highlight underline underline-offset-4 hover:opacity-80">photo gallery</Link>.
          </p>

          {/* FAQ */}
          <section className="mt-16" data-testid="location-faq">
            <h2 className="text-2xl sm:text-3xl tracking-tight text-foreground mb-4">
              Frequently asked questions
            </h2>
            <div>
              {faqs.map((f) => (
                <Faq key={f.q} q={f.q} a={f.a} />
              ))}
            </div>
          </section>
        </div>

        {/* Other locations — internal linking */}
        <div className="border-t border-surface/70 bg-surface/40">
          <div className="max-w-5xl mx-auto px-6 md:px-12 py-14">
            <h2 className="text-xl sm:text-2xl tracking-tight text-foreground mb-6">
              More Arlington & Northern Virginia neighborhoods we serve
            </h2>
            <div className="flex flex-wrap gap-2.5" data-testid="location-cross-links">
              {others.map((l) => (
                <Link
                  key={l.slug}
                  to={`/coffee-shop/${l.slug}`}
                  data-testid={`cross-link-${l.slug}`}
                  className="rounded-full border border-surface bg-background px-4 py-2 text-sm text-foreground/80 transition-colors hover:bg-highlight hover:text-foreground hover:border-highlight"
                >
                  {l.name}
                </Link>
              ))}
            </div>
            <Link
              to="/"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-highlight hover:opacity-80"
              data-testid="location-back-home"
            >
              <ArrowRight size={16} className="rotate-180" /> Back to Detour Coffee Co.
            </Link>
          </div>
        </div>
      </article>
    </>
  );
};

export default LocationPage;
